import React, { useState, useEffect, useRef } from 'react';
import { confirmErpia } from './ConfirmDialog';
import { Plus, X, Trash2, Settings } from 'lucide-react';
import APIService from '../services/api';
import { ancre } from '../tutoriel/ancres';

const BASE = 'https://besoins-tat-production.up.railway.app';

// ─── Mapping classe Tailwind → hex d'accent (les colonnes stockent des classes bg-*) ──
const TW_HEX = {
  'bg-slate-500': '#64748b', 'bg-slate-600': '#475569',
  'bg-blue-400': '#60a5fa', 'bg-blue-500': '#3b82f6', 'bg-blue-600': '#2563eb',
  'bg-indigo-500': '#6366f1', 'bg-indigo-600': '#4f46e5',
  'bg-violet-500': '#8b5cf6', 'bg-violet-600': '#7c3aed',
  'bg-purple-500': '#a855f7', 'bg-purple-600': '#9333ea',
  'bg-amber-500': '#f59e0b', 'bg-amber-600': '#d97706',
  'bg-orange-500': '#f97316', 'bg-orange-600': '#ea580c',
  'bg-green-500': '#22c55e', 'bg-green-600': '#16a34a',
  'bg-red-400': '#f87171', 'bg-red-500': '#ef4444',
  'bg-rose-500': '#f43f5e', 'bg-rose-600': '#e11d48',
  'bg-pink-500': '#ec4899',
  'bg-teal-500': '#14b8a6', 'bg-teal-600': '#0d9488',
  'bg-cyan-500': '#06b6d4', 'bg-cyan-600': '#0891b2',
};
const accentHex = (twClass) => TW_HEX[twClass] || '#64748b';
// Le ton d'une colonne sans couleur — « Sans colonne » n'est pas une étape.
const NEUTRE = accentHex(null);

// ─── Type de la carte : porté par la CARTE, plus déduit de sa colonne ───────
// Avant, « pré-vente » était une déduction (colonne « interactions » ou
// « besoin à venir »). Insuffisant : « Short list / Entretiens » est une
// étape de vrai besoin ET l'endroit où l'on pose une carte d'entretien
// client. Le serveur porte désormais `type_carte` ('besoin' | 'carte') ;
// une ligne d'avant la migration n'en a pas et vaut « besoin ».
const estCarte = (b) => String(b?.type_carte || 'besoin') === 'carte';

// dérive un rgba depuis un hex
const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
// thème : on lit data-theme pour ajuster les alphas (priorité clair)
const isDark = () => (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark');

// styles en-tête de colonne — barre teintée discrète (pas un bandeau plein)
const colHeadStyle = (accent) => {
  const dark = isDark();
  return { display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 7px 9px 12px',
    background: hexA(accent, dark ? 0.13 : 0.085), border: '1px solid ' + hexA(accent, dark ? 0.32 : 0.24), borderRadius: '9px' };
};
const colCountStyle = (accent) => {
  const dark = isDark();
  return { fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600, color: accent,
    background: hexA(accent, dark ? 0.24 : 0.16), padding: '1px 7px', borderRadius: '6px', flex: 'none' };
};

// ─── Styles Swiss du Kanban (scopés via préfixe kan-) ────────────────────────
const KAN_STYLES = `
.kan-root { display:flex; flex-direction:column; background:var(--bg); font-family:'IBM Plex Sans',system-ui,sans-serif; }
.kan-toolbar { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 24px; background:var(--surface); border-bottom:1px solid var(--border); flex-shrink:0; flex-wrap:wrap; }
.kan-toolbar-main { display:flex; align-items:center; gap:14px; min-width:0; flex-wrap:wrap; }
.kan-toolbar-actions { display:flex; align-items:center; gap:8px; margin-left:auto; }
.kan-summary { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.kan-summary-item { display:inline-flex; align-items:baseline; gap:5px; padding:4px 8px; border:1px solid var(--border); border-radius:7px; background:var(--surface); }
.kan-summary-value { font-family:'IBM Plex Mono',monospace; font-size:11px; font-weight:700; color:var(--text-strong); }
.kan-summary-label { font-size:10px; color:var(--text-3); }
.kan-title { margin:0; display:flex; align-items:center; gap:9px; font-family:'Archivo',sans-serif; font-size:18px; font-weight:800; letter-spacing:-0.02em; color:var(--text-strong); }
.kan-title .ats-dot { color:var(--brand); }
.kan-count { font-family:'IBM Plex Mono',monospace; font-size:11px; font-weight:600; color:var(--text-2); background:var(--chip-bg); padding:2px 9px; border-radius:6px; }
.kan-input { padding:8px 14px; border:1px solid var(--input-border); background:var(--input-bg); color:var(--text); border-radius:9px; font-size:13px; font-family:inherit; outline:none; }
.kan-input:focus { border-color:var(--text-strong); box-shadow:0 0 0 3px var(--accent-soft-bg); }
.kan-btn-ghost { display:flex; align-items:center; gap:7px; padding:8px 14px; border:1px solid var(--input-border); background:var(--ghost-bg); color:var(--text); border-radius:8px; font-size:13px; font-weight:500; font-family:inherit; cursor:pointer; white-space:nowrap; }
.kan-btn-ghost:hover { background:var(--ghost-bg-hover); }
.kan-btn-ghost.is-active { background:var(--accent); color:var(--accent-fg); border-color:var(--accent); }
.kan-btn-primary { display:flex; align-items:center; gap:7px; padding:8px 16px; border:none; background:var(--accent); color:var(--accent-fg); border-radius:8px; font-size:13px; font-weight:600; font-family:inherit; cursor:pointer; white-space:nowrap; }
.kan-btn-primary:disabled { opacity:.4; cursor:not-allowed; }
.kan-addcol { display:flex; flex-direction:column; gap:12px; padding:14px 24px; background:var(--surface); border-bottom:1px solid var(--hair); flex-shrink:0; }
.kan-cfg-chips { display:flex; flex-wrap:wrap; gap:8px; }
.kan-cfg-chip { display:flex; align-items:center; gap:7px; background:var(--surface); border:1px solid var(--border); border-radius:8px; padding:5px 9px; }
.kan-cfg-name { font-size:12.5px; color:var(--text); cursor:pointer; }
.kan-cfg-name:hover { color:var(--text-strong); }
.kan-cfg-input { font-size:12.5px; border:none; border-bottom:1px solid var(--accent); background:transparent; outline:none; color:var(--text); width:128px; font-family:inherit; }
.kan-cfg-x { background:transparent; border:none; color:var(--text-3); width:20px; height:20px; display:flex; align-items:center; justify-content:center; border-radius:5px; cursor:pointer; flex:none; }
.kan-cfg-x:hover { background:var(--danger-bg); color:var(--danger-fg); }
.kan-swatch { width:20px; height:20px; border-radius:6px; border:none; cursor:pointer; padding:0; }
.kan-swatch.sel { outline:2px solid var(--text-strong); outline-offset:1px; }
.kan-color-pop { position:absolute; display:flex; flex-wrap:wrap; gap:5px; background:var(--surface); border:1px solid var(--border); border-radius:9px; box-shadow:0 8px 28px var(--hover-shadow); padding:8px; z-index:200; width:140px; }
.kan-board { display:flex; gap:16px; overflow-x:auto; overflow-y:auto; padding:16px; flex:1; min-height:0; align-items:flex-start; background:var(--bg); overscroll-behavior:contain; }
.kan-col { flex-shrink:0; width:280px; display:flex; flex-direction:column; gap:8px; transition:opacity .18s ease, transform .18s ease; }
.kan-col-name { font-size:13px; font-weight:700; color:var(--text-strong); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:pointer; }
.kan-col-name:hover { text-decoration:underline; }
.kan-col-sq { width:8px; height:8px; border-radius:2px; flex-shrink:0; }
.kan-col-btn { background:transparent; border:none; color:var(--text-2); width:24px; height:24px; display:flex; align-items:center; justify-content:center; border-radius:6px; cursor:pointer; flex:none; }
.kan-col-btn:hover { background:var(--ghost-bg-hover); color:var(--text); }
.kan-col-edit { font-size:13px; font-weight:600; border:1px solid var(--input-border); background:var(--input-bg); color:var(--text); border-radius:7px; padding:2px 7px; outline:none; width:100%; min-width:0; font-family:inherit; }
.kan-col-edit:focus { border-color:var(--text-strong); box-shadow:0 0 0 3px var(--accent-soft-bg); }
.kan-cards { display:flex; flex-direction:column; gap:8px; min-height:8px; padding:2px 2px 10px; }
.kan-card-slot { border-radius:4px; transition:border-color .14s ease, padding-top .14s ease; }
.kan-empty { min-height:72px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; border:1.5px dashed var(--border); border-radius:10px; padding:14px; text-align:center; font-size:12px; color:var(--text-faint); transition:all .14s ease; }
.kan-empty-action { border:1px solid var(--input-border); background:var(--ghost-bg); color:var(--text-2); border-radius:7px; padding:5px 9px; font:600 11px 'IBM Plex Sans',system-ui,sans-serif; cursor:pointer; }
.kan-empty-action:hover { background:var(--ghost-bg-hover); color:var(--text); }
.kan-orphelins-note { margin:0 0 9px; font-size:11.5px; line-height:1.45; color:var(--text-2); }
.kan-orphelins-etapes { display:block; margin-top:4px; font:500 10.5px 'IBM Plex Mono',ui-monospace,monospace; color:var(--text-3); overflow-wrap:anywhere; }
.kan-card { background:var(--surface); border:1px solid var(--border); border-radius:10px; padding:14px; cursor:pointer; transition:transform .15s ease, border-color .15s ease, box-shadow .15s ease; user-select:none; overflow:visible; animation:kanCardIn .24s ease both; animation-delay:var(--kan-stagger,0ms); }
.kan-card:hover { border-color:var(--row-hover-bd); transform:translateY(-1px); box-shadow:0 4px 16px var(--hover-shadow); }
.kan-card:focus-visible { outline:2px solid var(--accent); outline-offset:2px; }
.kan-card-pre { border-style:dashed; }
.kan-type-btn { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:600; padding:2px 9px; border-radius:6px; background:var(--chip-bg); color:var(--text-3); border:1px solid transparent; font-family:inherit; cursor:pointer; }
.kan-type-btn:hover { border-color:var(--input-border); color:var(--text-2); }
.kan-card-drag { opacity:.42; transform:rotate(2deg) scale(.985); }
/* Menu « … » ouvert (vécu 28.09 : « Déplacer vers » passait SOUS la carte
   suivante). L'animation d'entrée laisse une transformation sur chaque carte,
   donc chaque carte est son propre plan d'empilement : le z-index du menu ne
   vaut qu'à l'intérieur de SA carte, et la carte d'après, peinte ensuite, le
   recouvrait. C'est la carte au menu ouvert qui passe devant ses voisines. */
.kan-card--menu { position:relative; z-index:40; }
.kan-pre-tag { display:inline-flex; align-items:center; gap:5px; font-size:11px; font-weight:600; padding:2px 9px; border-radius:6px; background:var(--chip-bg); color:var(--text-3); }
.kan-client { font-size:14px; font-weight:600; color:var(--text-strong); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:text; }
.kan-client.muted { color:var(--text-2); }
.kan-poste { font-size:12.5px; color:var(--text-2); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:text; }
.kan-client:hover, .kan-poste:hover { text-decoration:underline; text-decoration-thickness:1px; text-underline-offset:2px; }
.kan-edit-input { width:100%; font-size:13px; border:1px solid var(--input-border); background:var(--input-bg); color:var(--text); border-radius:7px; padding:4px 8px; outline:none; font-family:inherit; box-sizing:border-box; }
.kan-edit-input:focus { border-color:var(--text-strong); box-shadow:0 0 0 3px var(--accent-soft-bg); }
.kan-icon-btn { background:transparent; border:none; padding:4px; border-radius:6px; color:var(--text-3); cursor:pointer; display:flex; align-items:center; justify-content:center; }
.kan-icon-btn:hover { background:var(--ghost-bg-hover); color:var(--text); }
.kan-foot { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-top:11px; font-size:12px; color:var(--text-2); }
.kan-resp { display:inline-flex; align-items:center; gap:6px; min-width:0; }
.kan-resp-av { width:22px; height:22px; border-radius:6px; flex-shrink:0; display:flex; align-items:center; justify-content:center; background:var(--accent-soft-bg); color:var(--text-strong); font-size:11px; font-weight:600; font-family:'IBM Plex Mono',monospace; text-transform:uppercase; }
.kan-resp-name { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:88px; }
.kan-chip { display:inline-flex; align-items:center; gap:5px; font-size:11.5px; padding:2px 9px; border-radius:6px; background:var(--chip-bg); color:var(--chip-fg); }
.kan-date { margin:6px 0 0; font-size:11px; color:var(--text-faint); text-align:right; }
.kan-menu { position:absolute; right:0; top:26px; background:var(--surface); border:1px solid var(--border); border-radius:11px; box-shadow:0 8px 28px var(--hover-shadow); z-index:200; padding:5px; width:208px; }
.kan-menu-label { font-size:11px; color:var(--text-3); padding:4px 10px; font-weight:600; }
.kan-menu-item { width:100%; text-align:left; font-size:12.5px; padding:7px 10px; border-radius:7px; background:transparent; border:none; color:var(--text); cursor:pointer; display:flex; align-items:center; gap:8px; font-family:inherit; }
.kan-menu-item:hover { background:var(--ghost-bg-hover); }
.kan-menu-item.danger { color:var(--danger-fg); }
.kan-menu-item.danger:hover { background:var(--danger-bg); }
.kan-menu-dot { width:8px; height:8px; border-radius:2px; flex-shrink:0; }
.kan-menu-sep { border-top:1px solid var(--hair); margin:5px 0; }
.kan-qc { background:var(--surface); border:1px solid var(--accent); border-radius:10px; padding:12px; display:flex; flex-direction:column; gap:8px; }
.kan-qc-input { width:100%; padding:8px 11px; border:1px solid var(--input-border); background:var(--input-bg); color:var(--text); border-radius:8px; font-size:13px; font-family:inherit; outline:none; box-sizing:border-box; }
.kan-qc-input:focus { border-color:var(--text-strong); box-shadow:0 0 0 3px var(--accent-soft-bg); }
textarea.kan-qc-input { resize:none; min-height:64px; }
.kan-qc-link { background:none; border:none; padding:0; font-size:12px; color:var(--text-2); font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
.kan-qc-link:hover { color:var(--text-strong); }
.kan-qc-row { display:flex; gap:8px; padding-top:2px; }
.kan-qc-create { flex:1; padding:8px; border:none; background:var(--accent); color:var(--accent-fg); border-radius:8px; font-size:12.5px; font-weight:600; font-family:inherit; cursor:pointer; }
.kan-qc-create:disabled { opacity:.4; cursor:not-allowed; }
.kan-qc-cancel { flex:1; padding:8px; border:1px solid var(--input-border); background:var(--ghost-bg); color:var(--text); border-radius:8px; font-size:12.5px; font-weight:500; font-family:inherit; cursor:pointer; }
.kan-qc-cancel:hover { background:var(--ghost-bg-hover); }
.kan-spinner { width:40px; height:40px; border-radius:50%; border:2px solid var(--hair); border-bottom-color:var(--accent); animation:kanSpin .7s linear infinite; }
@keyframes kanSpin { to { transform:rotate(360deg); } }
@keyframes kanCardIn { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:translateY(0); } }
.kan-loading-wrap { display:grid; grid-template-columns:repeat(5,minmax(178px,232px)); gap:10px; padding:12px; overflow:hidden; }
.kan-loading-col { display:flex; flex-direction:column; gap:8px; }
.kan-skeleton { height:96px; border-radius:9px; background:linear-gradient(100deg,var(--surface) 30%,var(--ghost-bg-hover) 45%,var(--surface) 60%); background-size:200% 100%; border:1px solid var(--border); animation:kanShimmer 1.3s ease-in-out infinite; }
.kan-skeleton-head { height:38px; }
@keyframes kanShimmer { to { background-position:-200% 0; } }
@media (prefers-reduced-motion: reduce) { .kan-card, .kan-skeleton, .kan-col { animation:none; transition:none; } }
    /* Refonte board — densité de travail maîtrisée, surfaces pilotées par le shell. */
    .kan-toolbar{min-height:54px;padding:10px 16px;background:var(--header-bg);border-bottom-color:var(--border)}
    .kan-toolbar-main{gap:9px}.kan-summary{gap:4px}.kan-summary-item{padding:3px 6px}.kan-summary-label{font-size:9px}.kan-toolbar-actions{margin-left:auto}
    .kan-title{font-size:16px;letter-spacing:-.025em}.kan-count{font-size:10px;padding:3px 8px;background:var(--code-bg);color:var(--text-3)}
    .kan-board{gap:10px;padding:12px;background:var(--bg);scrollbar-color:var(--border) transparent}
    .kan-col{width:clamp(178px,15vw,232px);gap:7px}.kan-col>div[draggable]{min-height:38px}
    .kan-col-name{font-size:11.5px;letter-spacing:-.01em}.kan-col-sq{width:7px;height:7px}.kan-col-btn{width:22px;height:22px}
    .kan-cards{gap:7px}.kan-card{padding:11px 11px 10px;border-radius:9px}.kan-card:hover{box-shadow:0 7px 18px var(--hover-shadow)}
    .kan-pre-tag{font-size:9px;padding:3px 7px;text-transform:uppercase;letter-spacing:.045em}.kan-client{font-size:12.5px}.kan-poste{font-size:11px}.kan-foot{margin-top:9px;font-size:10.5px}.kan-resp-av{width:20px;height:20px;font-size:9px}.kan-resp-name{max-width:72px}.kan-chip{font-size:10px;padding:2px 6px}.kan-date{font-size:9px;margin-top:5px}
    @media(max-width:760px){.kan-toolbar{align-items:stretch;flex-direction:column}.kan-toolbar-main,.kan-toolbar-actions{width:100%;margin-left:0}.kan-toolbar-actions{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:stretch}.kan-toolbar-actions>.relative{grid-column:span 2}.kan-toolbar-actions .kan-input{width:100%!important}.kan-board{padding:10px}.kan-col{width:190px}}
    
`;

const DEFAULT_COLONNES = [
  { id: 'interactions',  label: 'Interactions / RV clients', color: 'bg-slate-500' },
  { id: 'besoin_venir',  label: 'Besoin à venir',            color: 'bg-blue-400' },
  { id: 'besoin_p1',     label: 'Besoin P1',                 color: 'bg-indigo-500' },
  { id: 'besoin_p2',     label: 'Besoin P2',                 color: 'bg-violet-500' },
  { id: 'propose',       label: 'Propositions envoyées',     color: 'bg-amber-500' },
  { id: 'shortlist',     label: 'Short list / Entretiens',   color: 'bg-orange-500' },
  { id: 'gagne',         label: 'Missions / Projets gagnés', color: 'bg-green-500' },
  { id: 'perdu',         label: 'Perdu / Annulé',            color: 'bg-red-400' },
];

const COLORS = ['bg-slate-500','bg-blue-400','bg-indigo-500','bg-violet-500','bg-amber-500','bg-orange-500','bg-green-500','bg-red-400','bg-pink-500','bg-teal-500','bg-cyan-500'];

// Inline editable column label
function KanbanColLabel({ label, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(label);
  const commit = () => { if (val.trim()) onSave(val.trim()); setEditing(false); };
  if (editing) return (
    <input autoFocus value={val} onChange={e => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(label); setEditing(false); } }}
      onClick={e => e.stopPropagation()}
      className="kan-col-edit"
    />
  );
  return (
    <span className="kan-col-name"
      onClick={e => { e.stopPropagation(); setVal(label); setEditing(true); }}
      title="Cliquer pour renommer la colonne">
      {label}
    </span>
  );
}

function QuickCreateCard({ colId, onSave, onCancel }) {
  const [titre, setTitre]     = React.useState('');
  const [client, setClient]   = React.useState('');
  const [notes, setNotes]     = React.useState('');
  // Un BESOIN naît avec sa date de démarrage souhaitée et son salaire max
  // (obligatoires côté serveur, 02.10) ; une carte n'en a pas.
  const [dateDemarrage, setDateDemarrage] = React.useState('');
  const [salaireMax, setSalaireMax] = React.useState('');
  const [expanded, setExpanded] = React.useState(false);
  // Pré-sélection d'après la colonne — une commodité, pas une règle : le type
  // reste modifiable ici et sur la carte. La colonne historique des
  // interactions propose « Carte », partout ailleurs c'est « Besoin ».
  const [type, setType] = React.useState(colId === 'interactions' ? 'carte' : 'besoin');
  const chip = (k) => k === type
    ? { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '6px', border: '1px solid transparent', background: 'var(--accent)', color: 'var(--accent-fg)', fontSize: '11px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }
    : { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--input-border)', background: 'transparent', color: 'var(--text-3)', fontSize: '11px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' };

  return (
    <div className="kan-qc">
      <div style={{ display: 'flex', gap: '6px', marginBottom: '2px' }}>
        <button onClick={() => setType('besoin')} style={chip('besoin')} title="Mandat à pourvoir : écran Besoins, matching, sourcing">
          <i className="bi bi-briefcase" /> Besoin
        </button>
        <button onClick={() => setType('carte')} style={chip('carte')} title="Rendez-vous, note, relance : reste dans le pipeline">
          <i className="bi bi-bookmark" /> Carte
        </button>
      </div>
      <input autoFocus value={titre} onChange={e => setTitre(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
        placeholder={type === 'carte' ? 'Objet du rendez-vous, de la note...' : 'Titre du besoin...'}
        className="kan-qc-input" />
      {/* Le client n'a jamais été facultatif : le serveur refuse sans lui
          (« Titre et client requis »), et la carte se taisait. */}
      <input value={client} onChange={e => setClient(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
        placeholder="Client..."
        className="kan-qc-input" />
      {type === 'besoin' && (
        <>
          {/* L'un sous l'autre : la carte est trop étroite pour deux champs. */}
          <input type="date" value={dateDemarrage} onChange={e => setDateDemarrage(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
            aria-label="Démarrage souhaité" title="Démarrage souhaité"
            className="kan-qc-input" />
          <input value={salaireMax} onChange={e => setSalaireMax(e.target.value)} inputMode="numeric"
            onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
            aria-label="Salaire max, CHF brut par an" placeholder="Salaire max (CHF / an)"
            className="kan-qc-input" />
        </>
      )}

      {/* Toggle détails */}
      {!expanded ? (
        <button onClick={() => setExpanded(true)} className="kan-qc-link">
          <i className="bi bi-plus-lg" /> Ajouter des détails
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Notes : contexte, budget, contact client, urgence..."
            rows={3}
            className="kan-qc-input" />
          <button onClick={() => { setExpanded(false); setNotes(''); }} className="kan-qc-link">
            <i className="bi bi-dash-lg" /> Masquer
          </button>
        </div>
      )}

      <div className="kan-qc-row">
        <button onClick={() => onSave(titre, client, notes, type, type === 'besoin' ? { dateDemarrage, salaireMax } : {})}
          disabled={!titre.trim() || !client.trim() || (type === 'besoin' && (!dateDemarrage || !String(salaireMax).trim()))}
          title={type === 'besoin' ? 'Titre, client, démarrage souhaité et salaire max sont obligatoires' : 'Titre et client sont obligatoires'}
          className="kan-qc-create">
          Créer
        </button>
        <button onClick={onCancel} className="kan-qc-cancel">
          Annuler
        </button>
      </div>
    </div>
  );
}

export default function KanbanView({ showNotification, onSelectBesoin, onCreateBesoin }) {
  const [quickCreate, setQuickCreate] = useState(null); // { colId, titre }
  const [besoins, setBesoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [searchPipeline, setSearchPipeline] = useState('');

  const filteredBesoins = searchPipeline.trim()
    ? besoins.filter(b => `${b.client} ${b.titre}`.toLowerCase().includes(searchPipeline.toLowerCase()))
    : besoins;
  // « 100/100 besoins · 85 à traiter » comptait les 41 rendez-vous de la
  // colonne « RV clients ». Un compteur qui ment sur ce qu'il compte est pire
  // qu'une absence de compteur : besoins et cartes sont désormais séparés.
  const estBesoin = b => estCarte(b) === false;
  const besoinsSeuls = besoins.filter(estBesoin);
  const cartesSeules = besoins.filter(estCarte);
  const activeBesoins = besoinsSeuls.filter(b => !['perdu', 'annule', 'archive'].includes(b.statut_pipeline)).length;
  const priorityBesoins = besoinsSeuls.filter(b => ['besoin_p1', 'besoin_p2'].includes(b.statut_pipeline)).length;
  const [dragOver, setDragOver] = useState(null);
  const [colonnes, setColonnes] = useState(DEFAULT_COLONNES);
  // Tant que les colonnes de l'organisation ne sont pas lues, une carte d'une
  // colonne personnalisée passerait pour « sans colonne » le temps d'un éclair.
  const [colonnesPretes, setColonnesPretes] = useState(false);
  const [showColConfig, setShowColConfig] = useState(false);
  const [newColLabel, setNewColLabel] = useState('');
  const [newColColor, setNewColColor] = useState('bg-blue-400');
  const [editingCol, setEditingCol] = useState(null);
  const [colorPickerCol, setColorPickerCol] = useState(null);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` };

  const fetchColonnes = async () => {
    try {
      const res = await fetch(`${BASE}/config/kanban-colonnes`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.colonnes && data.colonnes.length > 0) {
          setColonnes(data.colonnes);
          localStorage.removeItem('kanban_colonnes');
        } else {
          const local = localStorage.getItem('kanban_colonnes');
          if (local) {
            const parsed = JSON.parse(local);
            if (parsed && parsed.length > 0) {
              setColonnes(parsed);
              await fetch(`${BASE}/config/kanban-colonnes`, {
                method: 'PUT', headers,
                body: JSON.stringify({ colonnes: parsed })
              });
              localStorage.removeItem('kanban_colonnes');
            }
          }
        }
      }
    } catch(e) {
      try {
        const local = JSON.parse(localStorage.getItem('kanban_colonnes'));
        if (local && local.length > 0) setColonnes(local);
      } catch {}
    }
    setColonnesPretes(true);
  };

  useEffect(() => { fetchColonnes(); fetchBesoins(); }, []);

  const saveColonnes = async (cols) => {
    setColonnes(cols);
    try {
      const r = await fetch(`${BASE}/config/kanban-colonnes`, {
        method: 'PUT', headers,
        body: JSON.stringify({ colonnes: cols })
      });
      // Un refus du serveur (aucune colonne, deux fois le même identifiant)
      // se dit, et l'écran revient aux colonnes enregistrées.
      if (r.status === 400) {
        const j = await r.json().catch(() => ({}));
        showNotification(j.error || 'Colonnes non enregistrées.', 'error');
        fetchColonnes();
      }
    } catch(e) {
      localStorage.setItem('kanban_colonnes', JSON.stringify(cols));
    }
  };

  // Le pipeline est le SEUL écran où un rendez-vous a sa place à côté d'un
  // mandat : il demande explicitement les deux (?type=tous). Partout ailleurs
  // le serveur ne rend que les besoins.
  // TOUTES LES PAGES (01.10 — « des cartes gagnées ont été perdues ») : le
  // pipeline demandait limit=100 et le serveur rend les lignes modifiées le
  // plus récemment d'abord. Au-delà de cent, les cartes qu'on ne retouche
  // plus (les missions gagnées) sortaient du tableau sans un mot.
  const fetchBesoins = async () => {
    setLoading(true);
    try {
      const tout = [];
      for (let page = 1, pages = 1; page <= pages && page <= 20; page += 1) {
        const r = await fetch(`${BASE}/besoins?type=tous&page=${page}&limit=500`, { headers });
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
        tout.push(...(Array.isArray(data) ? data : (data.data || data.besoins || [])));
        pages = Number(data?.pagination?.pages) || 1;
      }
      const vus = new Set();
      setBesoins(tout.filter(b => !vus.has(b.id) && vus.add(b.id)));
    } catch (e) { showNotification('Erreur chargement besoins', 'error'); }
    setLoading(false);
  };

  const updatePipeline = async (besoinId, newStatut) => {
    try {
      await fetch(`${BASE}/besoins/${besoinId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ statut_pipeline: newStatut })
      });
      setBesoins(prev => prev.map(b => b.id === besoinId ? { ...b, statut_pipeline: newStatut } : b));
      if (newStatut === 'besoin_p1' || newStatut === 'besoin_p2') {
        const besoin = besoins.find(b => b.id === besoinId);
        await fetch(`${BASE}/notifications/pipeline`, {
          method: 'POST', headers,
          body: JSON.stringify({ besoinId, titreBesoin: besoin?.titre, client: besoin?.client, statut: newStatut, label: newStatut === 'besoin_p1' ? 'Besoin P1' : 'Besoin P2' })
        }).catch(() => {});
      }
    } catch (e) { showNotification('Erreur mise à jour', 'error'); }
  };

  const deleteBesoin = async (besoinId, titreBesoin) => {
    if (!await confirmErpia({ message: `Supprimer définitivement "${titreBesoin}" ?` })) return;
    try {
      await fetch(`${BASE}/besoins/${besoinId}`, { method: 'DELETE', headers });
      setBesoins(prev => prev.filter(b => b.id !== besoinId));
      showNotification('Besoin supprimé', 'success');
    } catch (e) { showNotification('Erreur suppression', 'error'); }
  };

  // Changement de type. Le serveur REFUSE de rétrograder un besoin déjà
  // travaillé (candidats positionnés, documents) : on affiche son message tel
  // quel — il nomme ce qui retient la ligne — et on remet la carte comme elle
  // était.
  const changerType = async (besoin, type) => {
    const avant = besoin.type_carte || 'besoin';
    setBesoins(prev => prev.map(b => b.id === besoin.id ? { ...b, type_carte: type } : b));
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ type_carte: type }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) throw new Error(data.error || `HTTP ${r.status}`);
      showNotification(data.message || 'Type mis à jour', 'success');
    } catch (e) {
      setBesoins(prev => prev.map(b => b.id === besoin.id ? { ...b, type_carte: avant } : b));
      showNotification(e.message, 'error');
    }
  };

  const quickPatch = async (besoinId, patch) => {
    setBesoins(prev => prev.map(b => b.id === besoinId ? { ...b, ...patch } : b));
    try {
      await fetch(`${BASE}/besoins/${besoinId}`, {
        method: 'PUT', headers,
        body: JSON.stringify(patch)
      });
    } catch (e) { showNotification('Erreur sauvegarde', 'error'); }
  };

  // Drag colonnes
  const [draggingCol, setDraggingCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const onColDragStart = (e, colId) => {
    setDraggingCol(colId);
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
  };
  const onColDragOver = (e, colId) => {
    if (!draggingCol || draggingCol === colId) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverCol(colId);
  };
  const onColDrop = (e, colId) => {
    e.stopPropagation();
    if (!draggingCol || draggingCol === colId) { setDraggingCol(null); setDragOverCol(null); return; }
    const from = colonnes.findIndex(c => c.id === draggingCol);
    const to   = colonnes.findIndex(c => c.id === colId);
    if (from === -1 || to === -1) return;
    const reordered = [...colonnes];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    saveColonnes(reordered);
    setDraggingCol(null); setDragOverCol(null);
  };

  const onDragStart = (e, besoin) => { setDragging(besoin); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, colId, cardId = null) => {
    e.preventDefault();
    setDragOver(colId);
    if (cardId) setDragOverCard(cardId);
  };
  const [dragOverCard, setDragOverCard] = React.useState(null);

  const onDrop = (e, colId, targetCardId = null) => {
    e.preventDefault();
    if (!dragging) { setDragging(null); setDragOver(null); setDragOverCard(null); return; }

    if (dragging.statut_pipeline !== colId) {
      // Moving to different column
      updatePipeline(dragging.id, colId);
    } else if (targetCardId && targetCardId !== dragging.id) {
      // Reorder within same column
      const cards = besoinsByCol(colId);
      const ids = cards.map(c => c.id);
      const fromIdx = ids.indexOf(dragging.id);
      const toIdx = ids.indexOf(targetCardId);
      if (fromIdx !== -1 && toIdx !== -1) {
        const newOrder = [...ids];
        newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, dragging.id);
        setColOrders(prev => ({ ...prev, [colId]: newOrder }));
        // Persister en DB : sauvegarder kanban_order pour chaque carte de la colonne
        newOrder.forEach(async (id, idx) => {
          await fetch(`${BASE}/besoins/${id}`, {
            method: 'PUT', headers,
            body: JSON.stringify({ kanban_order: idx })
          }).catch(() => {});
        });
      }
    }
    setDragging(null); setDragOver(null); setDragOverCard(null);
  };
  const onDragEnd = () => { setDragging(null); setDragOver(null); setDragOverCard(null); setDragOverCol(null); };

  const PRIO_ORDER = { high: 0, medium: 1, low: 2 };

  // Ordre manuel par colonne (drag within same col)
  const [colOrders, setColOrders] = React.useState({});

  const besoinsByCol = (colId) => {
    const cards = filteredBesoins.filter(b => (b.statut_pipeline || 'besoin_p1') === colId);
    const manualOrder = colOrders[colId];
    if (manualOrder) {
      return [...cards].sort((a, b) => {
        const ia = manualOrder.indexOf(a.id);
        const ib = manualOrder.indexOf(b.id);
        if (ia === -1 && ib === -1) return (a.kanban_order ?? 999) - (b.kanban_order ?? 999) || (PRIO_ORDER[a.priorite] ?? 1) - (PRIO_ORDER[b.priorite] ?? 1);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    }
    // Tri par kanban_order persisté, puis par priorité
    return [...cards].sort((a, b) =>
      (a.kanban_order ?? 999) - (b.kanban_order ?? 999) ||
      (PRIO_ORDER[a.priorite] ?? 1) - (PRIO_ORDER[b.priorite] ?? 1)
    );
  };

  // UNE CARTE N'EST JAMAIS INVISIBLE. Une carte dont l'étape ne correspond à
  // aucune colonne (colonne supprimée, renommée ailleurs, ancienne étape
  // « annule »…) n'était dessinée nulle part : pour l'écran, elle n'existait
  // plus. Elle va dans « Sans colonne », d'où on la glisse à sa place.
  const idsColonnes = new Set(colonnes.map(c => c.id));
  const horsColonnes = colonnesPretes
    ? filteredBesoins.filter(b => !idsColonnes.has(b.statut_pipeline || 'besoin_p1'))
    : [];
  const etapesInconnues = [...new Set(horsColonnes.map(b => b.statut_pipeline || 'besoin_p1'))];

  const addColonne = () => {
    if (!newColLabel.trim()) return;
    const id = newColLabel.toLowerCase().replace(/[^a-z0-9]/g, '_') + '_' + Date.now();
    saveColonnes([...colonnes, { id, label: newColLabel.trim(), color: newColColor }]);
    setNewColLabel('');
  };

  const removeColonne = async (colId) => {
    const count = besoinsByCol(colId).length;
    if (count > 0 && !await confirmErpia({ message: `Cette colonne contient ${count} besoin(s). Les déplacer en P1 et supprimer la colonne ?` })) return;
    if (count > 0) besoins.filter(b => (b.statut_pipeline || 'besoin_p1') === colId).forEach(b => updatePipeline(b.id, 'besoin_p1'));
    saveColonnes(colonnes.filter(c => c.id !== colId));
  };

  const updateColonneLabel = (colId, label) => {
    saveColonnes(colonnes.map(c => c.id === colId ? { ...c, label } : c));
    setEditingCol(null);
  };

  if (loading) return (
    <div className="kan-root" style={{ height: '100%', overflow: 'hidden' }}>
      <style>{KAN_STYLES}</style>
      <div className="kan-loading-wrap" aria-label="Chargement du pipeline">
        {Array.from({ length: 5 }).map((_, index) => (
          <div className="kan-loading-col" key={index}>
            <div className="kan-skeleton kan-skeleton-head" />
            <div className="kan-skeleton" />
            <div className="kan-skeleton" />
            <div className="kan-skeleton" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="kan-root" style={{ height: "100%", overflow: "hidden" }}>
      <style>{KAN_STYLES}</style>
      {/* Toolbar */}
      <div className="kan-toolbar">
        <div className="kan-toolbar-main">
          <i className="bi bi-kanban" style={{ fontSize: '18px', color: 'var(--text-2)' }} />
          <h1 className="kan-title">Pipeline commercial<span className="ats-dot">.</span></h1>
          <span className="kan-count" data-testid="text-pipeline-count">{besoinsSeuls.length} besoin{besoinsSeuls.length > 1 ? 's' : ''}</span>
          <div className="kan-summary" aria-label="Résumé du pipeline">
            <span className="kan-summary-item"><strong className="kan-summary-value">{activeBesoins}</strong><span className="kan-summary-label">à traiter</span></span>
            <span className="kan-summary-item"><strong className="kan-summary-value">{priorityBesoins}</strong><span className="kan-summary-label">priorités</span></span>
            {cartesSeules.length > 0 && (
              <span className="kan-summary-item" title="Rendez-vous, notes et relances : dans le pipeline, hors de l'écran Besoins et du matching">
                <strong className="kan-summary-value">{cartesSeules.length}</strong><span className="kan-summary-label">cartes</span>
              </span>
            )}
          </div>
        </div>
        <div className="kan-toolbar-actions">
          {/* Barre de recherche */}
          <div className="relative">
            <i className="bi bi-search" style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: 'var(--text-faint)' }} />
            <input
              value={searchPipeline}
              onChange={e => setSearchPipeline(e.target.value)}
              placeholder="Rechercher client, titre..."
              className="kan-input" style={{ paddingLeft: '34px', width: '240px' }}
              aria-label="Rechercher dans le pipeline" data-testid="input-pipeline-search"
            />
          </div>
          <button onClick={() => onCreateBesoin ? onCreateBesoin() : setQuickCreate({ colId: colonnes[0]?.id, titre: '' })}
            className="kan-btn-primary" data-testid="button-create-besoin">
            <Plus className="w-4 h-4" /> Nouveau besoin
          </button>
          <button onClick={() => setShowColConfig(v => !v)}
            className={`kan-btn-ghost ${showColConfig ? 'is-active' : ''}`} data-testid="button-pipeline-columns">
            <Settings className="w-4 h-4" /> Colonnes
          </button>
        </div>
      </div>

      {/* Config colonnes */}
      {showColConfig && (
        <div className="kan-addcol" onClick={() => setColorPickerCol(null)}>
          <div className="kan-cfg-chips">
            {colonnes.map(col => (
              <div key={col.id} className="kan-cfg-chip">
                {/* Color picker — clic pour ouvrir */}
                <div className="relative">
                  <button className="kan-swatch" style={{ background: accentHex(col.color) }}
                    onClick={e => { e.stopPropagation(); setColorPickerCol(colorPickerCol === col.id ? null : col.id); }} />
                  {colorPickerCol === col.id && (
                    <div className="kan-color-pop" style={{ left: 0, top: '26px' }} onClick={e => e.stopPropagation()}>
                      {COLORS.map(c => (
                        <button key={c}
                          onClick={e => { e.stopPropagation(); saveColonnes(colonnes.map(cc => cc.id === col.id ? {...cc, color: c} : cc)); setColorPickerCol(null); }}
                          className={`kan-swatch ${col.color === c ? 'sel' : ''}`} style={{ background: accentHex(c) }} />
                      ))}
                    </div>
                  )}
                </div>
                {editingCol === col.id ? (
                  <input autoFocus defaultValue={col.label} onBlur={e => updateColonneLabel(col.id, e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && updateColonneLabel(col.id, e.target.value)}
                    className="kan-cfg-input" />
                ) : (
                  <span className="kan-cfg-name" onClick={() => setEditingCol(col.id)}>{col.label}</span>
                )}
                <button onClick={() => removeColonne(col.id)} className="kan-cfg-x" title="Supprimer la colonne">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input value={newColLabel} onChange={e => setNewColLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addColonne()}
              placeholder="Nom de la nouvelle colonne..."
              className="kan-input" style={{ flex: 1, maxWidth: '280px' }} />
            <div className="flex gap-1">
              {COLORS.map(c => (
                <button key={c} onClick={() => setNewColColor(c)}
                  className={`kan-swatch ${newColColor === c ? 'sel' : ''}`} style={{ background: accentHex(c) }} />
              ))}
            </div>
            <button onClick={addColonne} disabled={!newColLabel.trim()} className="kan-btn-primary">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          </div>
        </div>
      )}

      {/* Kanban board */}
      <div {...ancre('pipeline')} className="kan-board">

        {colonnes.map(col => {
          const cards = besoinsByCol(col.id);
          const isOver = dragOver === col.id;
          const accent = accentHex(col.color);
          return (
            <div key={col.id}
              onDragOver={e => { if (draggingCol) onColDragOver(e, col.id); else onDragOver(e, col.id); }}
              onDrop={e => { if (draggingCol) onColDrop(e, col.id); else onDrop(e, col.id); }}
              onDragLeave={() => { setDragOver(null); setDragOverCol(null); }}
              className={`kan-col ${isOver && !draggingCol ? 'kan-col-drop-target' : ''}`}
              style={{ opacity: draggingCol === col.id ? 0.4 : 1 }}>

              {/* En-tête teinté discret — draggable */}
              <div draggable
                onDragStart={e => onColDragStart(e, col.id)}
                style={{ ...colHeadStyle(accent), cursor: 'grab',
                  outline: (dragOverCol === col.id && draggingCol) || (isOver && !draggingCol) ? `2px solid ${hexA(accent, 0.55)}` : 'none' }}>
                <span className="kan-col-sq" style={{ background: accent }} />
                <div className="flex items-center flex-1 min-w-0">
                  <KanbanColLabel label={col.label} onSave={label => saveColonnes(colonnes.map(c => c.id === col.id ? {...c, label} : c))} />
                </div>
                <span style={colCountStyle(accent)}>{cards.length}</span>
                {/* Color picker dans le header */}
                <div className="relative">
                  <button onClick={e => { e.stopPropagation(); setColorPickerCol(colorPickerCol === col.id ? null : col.id); }}
                    className="kan-col-btn" title="Changer la couleur">
                    <i className="bi bi-palette" style={{ fontSize: '13px' }} />
                  </button>
                  {colorPickerCol === col.id && (
                    <div className="kan-color-pop" style={{ right: 0, top: '30px' }} onClick={e => e.stopPropagation()}>
                      {COLORS.map(c => (
                        <button key={c}
                          onClick={e => { e.stopPropagation(); saveColonnes(colonnes.map(cc => cc.id === col.id ? {...cc, color: c} : cc)); setColorPickerCol(null); }}
                          className={`kan-swatch ${col.color === c ? 'sel' : ''}`} style={{ background: accentHex(c) }} />
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={e => { e.stopPropagation(); setQuickCreate(quickCreate?.colId === col.id ? null : { colId: col.id, titre: '' }); }}
                  className="kan-col-btn" title="Ajouter dans cette colonne (besoin ou carte)">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="kan-cards">
                {quickCreate?.colId === col.id && (
                  <QuickCreateCard
                    colId={col.id}
                    onSave={async (titre, client, notes, type, conditions = {}) => {
                      if (!titre.trim()) return;
                      try {
                        const res = await fetch(`${BASE}/besoins`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` },
                          body: JSON.stringify({ titre, client: client || '', description: notes || '', statut_pipeline: col.id, type_carte: type || 'besoin', ...conditions, responsableId: (() => { try { return JSON.parse(localStorage.getItem('user'))?.id; } catch(e) { return null; } })() })
                        });
                        const data = await res.json();
                        if (data.success || data.id || data.data?.id || data.besoin?.id) {
                          showNotification(type === 'carte' ? 'Carte créée' : 'Besoin créé', 'success');
                          setQuickCreate(null);
                          // EN TÊTE DE COLONNE (demande 28.09) : le serveur la
                          // range avant la première. L'ordre gardé ici après
                          // un glisser-déposer la mettrait en bas (inconnue de
                          // lui) — on l'oublie pour cette colonne : il est déjà
                          // enregistré côté serveur, rien ne se perd.
                          setColOrders(prev => { const n = { ...prev }; delete n[col.id]; return n; });
                          await fetchBesoins();
                        } else {
                          // Le refus du serveur dit quoi corriger (champ
                          // manquant, salaire mensuel au lieu d'annuel…) : la
                          // carte restait ouverte sans un mot.
                          showNotification(data.error || `Création impossible (HTTP ${res.status})`, 'error');
                        }
                      } catch(e) { showNotification('Erreur: ' + e.message, 'error'); }
                    }}
                    onCancel={() => setQuickCreate(null)}
                  />
                )}
                {cards.map((b, index) => (
                  <div key={b.id}
                    className="kan-card-slot"
                    onDragOver={e => onDragOver(e, col.id, b.id)}
                    onDrop={e => onDrop(e, col.id, b.id)}
                    style={{ borderTop: dragOverCard === b.id && dragging?.id !== b.id ? `2px solid ${accent}` : '2px solid transparent', borderRadius: '2px', transition: 'border-color .12s' }}>
                    <KanbanCard besoin={b} colonnes={colonnes} isDragging={dragging?.id === b.id}
                      onDragStart={onDragStart} onDragEnd={onDragEnd}
                      onMove={(newCol) => updatePipeline(b.id, newCol)}
                      onDelete={() => deleteBesoin(b.id, b.titre)}
                      onOpen={() => onSelectBesoin && onSelectBesoin(b)}
                      onQuickPatch={quickPatch}
                      onChangeType={(t) => changerType(b, t)}
                      style={{ '--kan-stagger': `${Math.min(index, 7) * 28}ms` }}
                    />
                  </div>
                ))}
                {cards.length === 0 && (
                  <div className="kan-empty" style={{ borderColor: isOver ? hexA(accent, 0.5) : 'var(--border)', color: isOver ? 'var(--text-2)' : 'var(--text-faint)' }}>
                    {isOver ? 'Déposer ici' : 'Rien dans cette colonne'}
                    {!searchPipeline && !isOver && <button onClick={e => { e.stopPropagation(); setQuickCreate({ colId: col.id, titre: '' }); }} className="kan-empty-action">Ajouter</button>}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {horsColonnes.length > 0 && (
          <div className="kan-col" data-testid="kanban-sans-colonne" aria-label="Cartes sans colonne">
            <div style={colHeadStyle(NEUTRE)}>
              <span className="kan-col-sq" style={{ background: NEUTRE }} />
              <div className="flex items-center flex-1 min-w-0"><span className="kan-col-name">Sans colonne</span></div>
              <span style={colCountStyle(NEUTRE)}>{horsColonnes.length}</span>
            </div>
            <div className="kan-cards">
              <p className="kan-orphelins-note">
                Leur étape n'existe plus dans le pipeline. Glissez chaque carte dans sa colonne, ou choisissez-la dans le menu de la carte.
                <span className="kan-orphelins-etapes">
                  {etapesInconnues.slice(0, 4).join(' · ')}{etapesInconnues.length > 4 ? ` · +${etapesInconnues.length - 4}` : ''}
                </span>
              </p>
              {horsColonnes.map((b, index) => (
                <div key={b.id} className="kan-card-slot">
                  <KanbanCard besoin={b} colonnes={colonnes} isDragging={dragging?.id === b.id}
                    onDragStart={onDragStart} onDragEnd={onDragEnd}
                    onMove={(newCol) => updatePipeline(b.id, newCol)}
                    onDelete={() => deleteBesoin(b.id, b.titre)}
                    onOpen={() => onSelectBesoin && onSelectBesoin(b)}
                    onQuickPatch={quickPatch}
                    onChangeType={(t) => changerType(b, t)}
                    style={{ '--kan-stagger': `${Math.min(index, 7) * 28}ms` }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const KanbanCard = ({ besoin, colonnes, isDragging, onDragStart, onDragEnd, onMove, onDelete, onOpen, onQuickPatch, onChangeType, style }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [editingTitre, setEditingTitre] = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const [tempTitre, setTempTitre] = useState('');
  const [tempClient, setTempClient] = useState('');
  const menuRef = useRef(null);
  const justDragged = useRef(false);

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const scoreColor = (s) => s >= 70 ? '#16a34a' : s >= 50 ? '#f97316' : '#ef4444';
  const nbCandidats = besoin.nb_candidats || besoin._count?.candidats || 0;
  // Le type vient de la CARTE (serveur), plus de sa colonne : « Short list /
  // Entretiens » porte aussi bien un vrai mandat qu'une carte d'entretien.
  const isPreSale = estCarte(besoin);

  const startEditTitre = (e) => { e.stopPropagation(); setTempTitre(besoin.titre || ''); setEditingTitre(true); };
  const commitTitre = () => { if (tempTitre.trim()) onQuickPatch(besoin.id, { titre: tempTitre.trim() }); setEditingTitre(false); };
  const startEditClient = (e) => { e.stopPropagation(); setTempClient(besoin.client || ''); setEditingClient(true); };
  const commitClient = () => { onQuickPatch(besoin.id, { client: tempClient.trim() }); setEditingClient(false); };

  // Ouvre le détail — corps de la carte cliquable. Bloqué juste après un drag (clic fantôme).
  const onCardClick = () => {
    if (justDragged.current) return;
    onOpen && onOpen();
  };

  return (
    <div draggable={!editingTitre && !editingClient}
      onDragStart={e => { e.stopPropagation(); justDragged.current = true; onDragStart(e, besoin); }}
      onDragEnd={e => { e.stopPropagation(); onDragEnd(e); requestAnimationFrame(() => { justDragged.current = false; }); }}
      onClick={onCardClick}
      role="button" tabIndex={0}
      onKeyDown={e => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCardClick(); } }}
      className={`kan-card ${isPreSale ? 'kan-card-pre' : ''} ${isDragging ? 'kan-card-drag' : ''} ${showMenu ? 'kan-card--menu' : ''}`}
      style={style}>

      {/* Le type se lit ET se change ici : un rendez-vous rangé par erreur en
          besoin se corrige sans quitter le pipeline. Le libellé dit ce que la
          ligne EST, l'infobulle ce que ça change. */}
      <div style={{ marginBottom: '8px' }}>
        <button className="kan-type-btn"
          onClick={e => { e.stopPropagation(); onChangeType && onChangeType(isPreSale ? 'besoin' : 'carte'); }}
          title={isPreSale
            ? 'Carte du pipeline : hors de l\'écran Besoins, du matching et du sourcing. Cliquer pour en faire un besoin.'
            : 'Besoin : mandat à pourvoir (écran Besoins, matching, sourcing). Cliquer pour en faire une carte.'}>
          <i className={`bi bi-${isPreSale ? 'bookmark' : 'briefcase'}`} /> {isPreSale ? 'Carte' : 'Besoin'}
        </button>
      </div>

      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          {editingClient ? (
            <input autoFocus value={tempClient} onChange={e => setTempClient(e.target.value)}
              onBlur={commitClient}
              onKeyDown={e => { if (e.key === 'Enter') commitClient(); if (e.key === 'Escape') { setTempClient(besoin.client||''); setEditingClient(false); } }}
              onClick={e => e.stopPropagation()}
              className="kan-edit-input"
            />
          ) : (
            <p className={`kan-client ${isPreSale ? 'muted' : ''}`}
              onClick={startEditClient} title="Cliquer pour modifier le client">
              {besoin.client || <span style={{ fontStyle: 'italic', fontWeight: 400, color: 'var(--text-faint)' }}>+ client</span>}
            </p>
          )}
          {editingTitre ? (
            <input autoFocus value={tempTitre} onChange={e => setTempTitre(e.target.value)}
              onBlur={commitTitre}
              onKeyDown={e => { if (e.key === 'Enter') commitTitre(); if (e.key === 'Escape') { setTempTitre(besoin.titre||''); setEditingTitre(false); } }}
              onClick={e => e.stopPropagation()}
              className="kan-edit-input" style={{ marginTop: '4px' }}
            />
          ) : (
            <p className="kan-poste" style={{ marginTop: '2px', fontStyle: 'italic' }}
              onClick={startEditTitre} title="Cliquer pour modifier le titre">
              {besoin.titre || <span style={{ color: 'var(--text-faint)', fontStyle: 'normal' }}>+ poste</span>}
            </p>
          )}
        </div>

        <div className="flex items-center gap-0.5 flex-shrink-0">
          {/* Menu déplacer */}
          <div className="relative" ref={menuRef}>
            <button onClick={e => { e.stopPropagation(); setShowMenu(v => !v); }} className="kan-icon-btn" title="Actions">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </button>
            {showMenu && (
              <div className="kan-menu" onClick={e => e.stopPropagation()}>
                <p className="kan-menu-label">Déplacer vers :</p>
                {colonnes.map(col => (
                  <button key={col.id} onClick={() => { onMove(col.id); setShowMenu(false); }}
                    className="kan-menu-item">
                    <span className="kan-menu-dot" style={{ background: accentHex(col.color) }} />
                    {col.label}
                  </button>
                ))}
                <div className="kan-menu-sep" />
                <button onClick={() => { onDelete(); setShowMenu(false); }} className="kan-menu-item danger">
                  <Trash2 className="w-3 h-3" /> Supprimer ce besoin
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="kan-foot">
        <span className="kan-resp">
          <span className="kan-resp-av">
            {(typeof besoin.responsable === 'string' ? besoin.responsable : besoin.responsable?.username || '?')[0]}
          </span>
          <span className="kan-resp-name">
            {typeof besoin.responsable === 'string' ? besoin.responsable : besoin.responsable?.username || '—'}
          </span>
        </span>
        <div className="flex items-center gap-2">
          {nbCandidats > 0 && (
            <span className="kan-chip"><i className="bi bi-people" /> {nbCandidats}</span>
          )}
          {besoin.score_moyen > 0 && (
            <span style={{ fontWeight: 700, fontSize: '12px', color: scoreColor(besoin.score_moyen) }}>{besoin.score_moyen}%</span>
          )}
        </div>
      </div>
      {besoin.created_at && (
        <p className="kan-date">
          {new Date(besoin.created_at).toLocaleDateString('fr-FR')}
        </p>
      )}
    </div>
  );
};
